<template>
  <li
    class="PlayerListItem"
    :data-testid="player.id"
  >
    <PlayerAvatar
      class="PlayerListItem__avatar"
      :modelValue="player"
      :hasAward="!!award"
      v-bind="{isClickable}"
    />
    <div
      class="PlayerListItem__content"
    >
      <div class="PlayerListItem__label">
        <component
          :is="isClickable ? 'RouterLink' : 'div'"
          class="PlayerListItem__name"
          data-testid="playerName"
          :to="isClickable ? getRouteForPlayer(player.id) : undefined"
          @click.stop.prevent="handlePlayerClick(player)"
        >
          {{ player.name }}
        </component>
        <button
          v-if="player.supporter_type"
          type="button"
          :aria-label="formattedSupporterStatus"
          @click="$emit('click:supporterBadge')"
        >
          <Icon
            class="PlayerListItem__icon"
            name="bookmark"
            :style="`--iconColor: var(--color${player.supporter_type}Background)`"
          />
        </button>
      </div>
      <AwardBadge
        v-if="award"
        class="PlayerListItem__awardBadge"
        :modelValue="award"
      />
      <div
        v-if="status?.note || subRequest?.run_invite?.player"
        class="PlayerListItem__meta"
        data-testid="playerMeta"
      >
        <div
          v-if="subRequest?.run_invite?.player"
          class="PlayerListItem__metaItem"
          data-testid="invitedByMeta"
          aria-expanded="false"
        >
          <Icon
            class="PlayerListItem__metaItemIcon"
            name="person"
          />
          <span class="PlayerListItem__metaItemText">
            Invited by <component
              :is="isClickable ? 'RouterLink' : 'span'"
              :to="isClickable ? getRouteForPlayer(subRequest.run_invite.player.id) : undefined"
              @click.stop.prevent="handlePlayerClick(subRequest.run_invite.player)"
            >{{ subRequest.run_invite.player.name }}</component>
          </span>
        </div>
        <div
          v-if="status?.note"
          class="PlayerListItem__metaItem"
          data-testid="noteMeta"
          :aria-expanded="isNoteExpanded"
          @click="handleNoteClick"
        >
          <Icon
            class="PlayerListItem__metaItemIcon"
            name="chatBubble"
          /> <span class="PlayerListItem__metaItemText">{{ status.note }}</span>
        </div>
      </div>
    </div>
    <div
      v-if="$slots.buttonList"
      class="PlayerListItem__statusButtonList"
    >
      <slot
        name="buttonList"
        v-bind="{player, status}"
      />
    </div>
  </li>
</template>

<script lang="ts" setup>
import Icon from "@/components/Icon.vue";
import haptic from "@/lib/utils/platform/haptic";
import getRouteForPlayer from "@/lib/utils/route/getRouteForPlayer";
import { Award, PlayerSummary, Status, SubRequestWithAssociations } from "@shared/lib/database/query.types";
import { computed, ref } from "vue";
import AwardBadge from "./AwardBadge.vue";
import PlayerAvatar from "./PlayerAvatar.vue";

const emit = defineEmits<{
  "click:player": [player: PlayerSummary],
  "click:supporterBadge": [],
}>();

const {
  player,
  isClickable = true,
} = defineProps<{
  player: PlayerSummary,
  status?: Status,
  subRequest?: SubRequestWithAssociations,
  award?: Award,
  isClickable?: boolean,
}>();

const isNoteExpanded = ref(false);

const formattedSupporterStatus = computed(() => {
  switch (player.name) {
    case "OfficialSponsor":
      return `${player.name} is an official sponsor`;
    case "Supporter":
    case "Believer":
    case "Backer":
    default:
      return `${player.name} is a ${player.supporter_type?.toLowerCase() || "supporter"}!`;
  }
});

function handleNoteClick () {
  haptic();
  isNoteExpanded.value = !isNoteExpanded.value;
}

function handlePlayerClick (player: PlayerSummary) {
  if (!isClickable) return;

  emit("click:player", player);
}
</script>

<style lang="scss" scoped>
.PlayerListItem {
  display: flex;
  align-items: start;
  padding: 8px 12px 8px 8px;

  outline: solid 1px var(--themeColorNeutral100);
  background-color: var(--themeColorWhite);

  font-family: var(--fontFamilyMono);
  font-weight: var(--fontWeightMedium);

  user-select: text;
}

.PlayerListItem__content {
  display: grid;
  gap: 4px;
  align-items: center;
  min-height: 48px;
  padding: 4px 0;

  font-size: 20px;
}

.PlayerListItem__avatar {
  flex-shrink: 0;
  margin-right: 12px;
}

.PlayerListItem__avatarImage {
  display: block;
  margin: 4px;
  border-radius: 50%;
}

.PlayerListItem__label {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.PlayerListItem__meta {
  overflow: hidden;
}

.PlayerListItem__metaItem {
  position: relative;

  display: flex;
  gap: 6px;

  color: var(--themeColorNeutral500);

  &[aria-expanded] {
    cursor: pointer;
  }
}

.PlayerListItem__metaItemIcon {
  --iconSize: 14px;
  flex: 0 0 auto;
  margin-top: var(--fontSafariOffset);
  color: var(--themeColorNeutral400);
}

.PlayerListItem__metaItemText {
  flex: 1 1 auto;
  padding-top: var(--fontSafariOffset);

  font-size: 14px;
  line-height: 1.2;

  .PlayerListItem__metaItem[aria-expanded="false"] & {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.PlayerListItem--placeholder {
  color: var(--themeColorNeutral400);
  font-family: var(--fontFamilyMono);
}

.PlayerListItem__name {
  display: block;
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.PlayerListItem__icon {
  --iconSize: 20px;
  position: relative;
  top: -1px;

  flex: 0 0 auto;
  margin-left: 8px;

  color: var(--iconColor);
}

.PlayerListItem__awardBadge {
  &:has(+ .PlayerListItem__meta) {
    margin-bottom: 2px;
  }
}

.PlayerListItem__statusButtonList {
  margin-left: auto;
  padding: 12px 0 12px 12px;
}
</style>
